<template>
    <section class="exhibitor-list" aria-label="Exhibitor list">
        <div class="datatable-toolbar exhibitor-toolbar">
            <div class="exhibitor-search">
                <label class="sr-only" for="exhibitor-search">Search exhibitors</label>
                <input id="exhibitor-search" type="search" class="form-control" placeholder="Search exhibitors" v-model="dataFilter.searchText" @keyup.enter="doFind">
                <button class="btn btn-outline-primary" type="button" @click="doFind">Search</button>
                <button v-if="dataFilter.searchText" class="btn btn-link text-secondary clear-search" type="button" @click="clearSearch">Clear</button>
            </div>

            <RouterLink to="/exhibitor/add" class="btn btn-primary add-exhibitor-button">
                <i class="pi pi-plus mr-2" aria-hidden="true"></i>
                Add exhibitor
            </RouterLink>
        </div>

        <div v-if="isLoading || dataList.length" class="table-responsive table-container exhibitor-table-wrapper">
            <table class="table table-striped exhibitor-table">
                <thead>
                <tr>
                    <th scope="col">No</th>
                    <th scope="col">Booth</th>
                    <th scope="col">Company</th>
                    <th scope="col">Product</th>
                    <th scope="col">Banner</th>
                    <th scope="col">Status</th>
                    <th scope="col">Actions</th>
                </tr>
                </thead>

                <tbody>
                <tr v-if="isLoading">
                    <td colspan="7" class="table-message">
                        <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                        Loading exhibitors…
                    </td>
                </tr>
                <tr v-else v-for="(data, index) in dataList" :key="data.id">
                    <td class="order-number">{{ index + 1 }}</td>
                    <td class="text-left exhibitor-booth">{{ data.boothNumber }}</td>
                    <td class="text-left exhibitor-company">{{ data.companyName }}</td>
                    <td class="text-left exhibitor-product">{{ data.productName }}</td>
                    <td class="banner-cell">
                        <Image v-if="data.image" :src="getImageUrl(data.image)" :alt="`Banner for ${data.companyName}`" width="72" preview />
                        <span v-else class="text-muted">—</span>
                    </td>
                    <td>
                        <span class="badge badge-success" v-if="data.status === 1">{{ data.statusLabel }}</span>
                        <span class="badge badge-dark" v-else>{{ data.statusLabel }}</span>
                    </td>
                    <td class="actions">
                        <button type="button" class="btn btn-outline-secondary" :aria-label="`Edit ${data.companyName}`" v-tooltip="'Edit exhibitor'" @click="onEdit(data)"><i class="pi pi-pencil" aria-hidden="true"></i></button>
                        <button type="button" class="btn btn-outline-danger" :aria-label="`Remove ${data.companyName}`" v-tooltip="'Remove exhibitor'" @click="onRemove(data)"><i class="pi pi-trash" aria-hidden="true"></i></button>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <div v-else class="exhibitor-empty-state" role="status">
            <i class="pi pi-briefcase" aria-hidden="true"></i>
            <h2>{{ dataFilter.searchText ? 'No exhibitors match your search.' : 'No exhibitors yet.' }}</h2>
            <p>{{ dataFilter.searchText ? 'Try a different keyword or clear the search.' : 'Add an exhibitor to start building your partner directory.' }}</p>
            <button v-if="dataFilter.searchText" class="btn btn-outline-primary" type="button" @click="clearSearch">Clear search</button>
            <RouterLink v-else to="/exhibitor/add" class="btn btn-primary">
                <i class="pi pi-plus mr-2" aria-hidden="true"></i>
                Add exhibitor
            </RouterLink>
        </div>

        <ConfirmDialog></ConfirmDialog>
    </section>
</template>

<script>
import {defineComponent} from 'vue'
import ConfirmDialog from "primevue/confirmdialog";
import apiService from "@/services/ApiService";
import Image from "primevue/image";

export default defineComponent({
    name: "ExhibitorList",
    components: {
        ConfirmDialog, Image
    },
    data() {
        return {
            dataList: [],
            isLoading: true,
            dataFilter: {
                searchText: ''
            }
        }
    },
    methods: {
        onEdit(data) {
            this.$router.push(`/exhibitor/edit/${data.id}`);
        },
        onRemove(data) {
            this.$confirm.require({
                message: `Remove ${data.companyName} from the exhibitor directory?`,
                header: 'Remove exhibitor',
                icon: 'pi pi-exclamation-triangle',
                acceptClass: 'p-button-danger',
                accept: () => this.doRemove(data.id)
            });
        },
        doFind() {
            const params = {
                SearchText: this.dataFilter.searchText
            };
            this.isLoading = true;
            apiService.get('/exhibitor', {params: params})
                .then(response => {
                    this.dataList = response.data || [];
                })
                .catch(error => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Failed to get exhibitors',
                        detail: error.response?.data?.Message || 'Please try again.',
                        life: 5000
                    });
                }).finally(() => {
                    this.isLoading = false;
                });
        },
        clearSearch() {
            this.dataFilter.searchText = '';
            this.doFind();
        },
        doRemove(id) {
            apiService.delete(`/exhibitor/${id}`)
                .then(() => {
                    this.doFind();
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Exhibitor has been removed',
                        life: 5000
                    });
                })
                .catch(error => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Failed to remove exhibitor',
                        detail: error.response?.data?.Message || 'Please try again.',
                        life: 5000
                    });
                });
        },
        getImageUrl(image){
            return `${apiService.defaults.baseURL}/file/${image}`;
        }
    },
    mounted() {
        this.doFind();
    }
})
</script>

<style scoped>
.exhibitor-toolbar {
    align-items: center;
    display: flex;
    gap: 0.75rem;
    justify-content: space-between;
    margin-bottom: 0.5rem;
}

.exhibitor-search {
    align-items: center;
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.exhibitor-search .form-control {
    max-width: 19rem;
}

.clear-search {
    font-size: 0.875rem;
    padding-left: 0.25rem;
    padding-right: 0.25rem;
}

.add-exhibitor-button {
    white-space: nowrap;
}

.exhibitor-table-wrapper {
    margin: 0;
}

.exhibitor-table th {
    white-space: nowrap;
}

.exhibitor-booth {
    font-weight: 600;
    min-width: 6rem;
}

.exhibitor-company {
    font-weight: 600;
    min-width: 13rem;
}

.exhibitor-product {
    min-width: 12rem;
}

.banner-cell {
    min-width: 7rem;
    text-align: center;
}

.table-message {
    color: #6c757d;
    padding: 2.5rem 1rem !important;
    text-align: center;
}

.exhibitor-empty-state {
    align-items: center;
    border: 1px solid #e2e6ec;
    border-radius: 0.65rem;
    color: #6c757d;
    display: flex;
    flex-direction: column;
    padding: 3rem 1.5rem;
    text-align: center;
}

.exhibitor-empty-state > .pi {
    color: #426cc6;
    font-size: 1.9rem;
    margin-bottom: 0.85rem;
}

.exhibitor-empty-state h2 {
    color: #343a40;
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0 0 0.35rem;
}

.exhibitor-empty-state p {
    margin: 0 0 1rem;
}

@media (max-width: 767.98px) {
    .exhibitor-toolbar {
        align-items: stretch;
        flex-direction: column;
    }

    .exhibitor-search .form-control {
        max-width: none;
        width: 100%;
    }

    .add-exhibitor-button {
        align-self: flex-start;
    }
}
</style>
